import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type LegalType = 'privacy' | 'terms';
interface LegalModalProps {
  type: LegalType | null;
  onClose: () => void;
}
export function LegalModal({
  type,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    if (type) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [type]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  return <AnimatePresence data-id="element-204">
      {type && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-navy/70 backdrop-blur-sm" onClick={onClose} data-id="element-205">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25
      }} className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col border border-gold/20" onClick={e => e.stopPropagation()} data-id="element-206">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-cream rounded-t-2xl" data-id="element-207">
              <h2 className="font-serif text-2xl font-bold text-navy" data-id="element-208">
                {type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
              </h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-navy/5 transition-colors" aria-label="Cerrar" data-id="element-209">
                <X className="w-5 h-5 text-navy" data-id="element-210" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto p-6 md:p-8 prose prose-navy max-w-none text-gray-700" data-id="element-211">
              {type === 'privacy' ? <PrivacyContent data-id="element-212" /> : <TermsContent data-id="element-213" />}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-cream rounded-b-2xl flex justify-end" data-id="element-214">
              <button onClick={onClose} className="px-6 py-2 bg-navy text-white rounded-full hover:bg-navy-light transition-colors font-medium" data-id="element-215">
                Entendido
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function PrivacyContent() {
  return <div className="space-y-4 text-sm md:text-base leading-relaxed" data-id="element-216">
      <p className="text-xs text-gray-500 italic" data-id="element-217">
        Última actualización: {new Date().toLocaleDateString('es-ES')}
      </p>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-218">
        1. Información que Recopilamos
      </h3>
      <p data-id="element-219">
        Anisley Estanque Insurance Services recopila información personal que
        usted nos proporciona voluntariamente al contactarnos, incluyendo:
        nombre completo, número telefónico, dirección de correo electrónico,
        fecha de nacimiento, información de ingresos y datos familiares
        necesarios para evaluar opciones de seguro.
      </p>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-220">
        2. Uso de la Información
      </h3>
      <p data-id="element-221">Utilizamos su información exclusivamente para:</p>
      <ul className="list-disc pl-6 space-y-1" data-id="element-222">
        <li data-id="element-223">Evaluar y recomendar planes de seguro adecuados a su situación.</li>
        <li data-id="element-224">Gestionar su aplicación y proceso de inscripción.</li>
        <li data-id="element-225">Comunicarnos con usted sobre su cobertura.</li>
        <li data-id="element-226">Cumplir con requisitos legales y regulatorios.</li>
      </ul>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-227">
        3. Compartir Información
      </h3>
      <p data-id="element-228">
        No vendemos ni alquilamos su información personal. Compartimos sus datos
        únicamente con compañías aseguradoras autorizadas para procesar su
        solicitud, y con autoridades cuando lo exija la ley.
      </p>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-229">
        4. Seguridad de los Datos
      </h3>
      <p data-id="element-230">
        Implementamos medidas de seguridad razonables para proteger su
        información contra acceso no autorizado, alteración o divulgación. Sin
        embargo, ninguna transmisión por internet es 100% segura.
      </p>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-231">
        5. Sus Derechos
      </h3>
      <p data-id="element-232">Usted tiene derecho a:</p>
      <ul className="list-disc pl-6 space-y-1" data-id="element-233">
        <li data-id="element-234">Solicitar acceso a la información que tenemos sobre usted.</li>
        <li data-id="element-235">Solicitar corrección o eliminación de sus datos.</li>
        <li data-id="element-236">Retirar su consentimiento en cualquier momento.</li>
      </ul>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-237">
        6. Cambios a esta Política
      </h3>
      <p data-id="element-238">
        Nos reservamos el derecho de actualizar esta Política de Privacidad en
        cualquier momento. Los cambios entrarán en vigor desde su publicación en
        este sitio.
      </p>
    </div>;
}
function TermsContent() {
  return <div className="space-y-4 text-sm md:text-base leading-relaxed" data-id="element-239">
      <p className="text-xs text-gray-500 italic" data-id="element-240">
        Última actualización: {new Date().toLocaleDateString('es-ES')}
      </p>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-241">
        1. Aceptación de los Términos
      </h3>
      <p data-id="element-242">
        Al utilizar este sitio web y los servicios de Anisley Estanque Insurance
        Services, usted acepta cumplir con estos términos y condiciones.
      </p>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-243">
        2. Servicios Ofrecidos
      </h3>
      <p data-id="element-244">
        Anisley Estanque es una agente de seguros licenciada que ofrece asesoría
        y representación para la contratación de seguros de vida, salud, dental
        y visión. Trabajamos con múltiples compañías aseguradoras autorizadas.
      </p>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-245">
        3. Sin Costo para el Cliente
      </h3>
      <p data-id="element-246">
        Nuestros servicios de asesoría son gratuitos para el cliente final.
        Recibimos comisiones directamente de las compañías aseguradoras, sin
        afectar el precio de su plan.
      </p>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-247">
        4. Información Proporcionada
      </h3>
      <p data-id="element-248">
        El contenido en este sitio es informativo y no constituye una oferta
        vinculante. Las coberturas, precios y elegibilidad están sujetos a
        aprobación por las aseguradoras y a las leyes aplicables en su estado.
      </p>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-249">
        5. Responsabilidad del Cliente
      </h3>
      <p data-id="element-250">
        Usted se compromete a proporcionar información verídica y completa. La
        información incorrecta puede afectar su elegibilidad o resultar en la
        cancelación de su cobertura.
      </p>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-251">
        6. Limitación de Responsabilidad
      </h3>
      <p data-id="element-252">
        Anisley Estanque no se hace responsable por decisiones tomadas con base
        en la información del sitio sin haber consultado directamente. Las
        pólizas finales son emitidas y administradas por las compañías
        aseguradoras.
      </p>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-253">
        7. Testimonios
      </h3>
      <p data-id="element-254">
        Los testimonios mostrados son experiencias reales de clientes. Los
        resultados pueden variar según la situación individual.
      </p>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-255">
        8. Modificaciones
      </h3>
      <p data-id="element-256">
        Nos reservamos el derecho de modificar estos términos en cualquier
        momento. El uso continuado del sitio implica aceptación de los cambios.
      </p>

      <h3 className="font-serif text-xl font-bold text-navy mt-4" data-id="element-257">
        9. Ley Aplicable
      </h3>
      <p data-id="element-258">
        Estos términos se rigen por las leyes del Estado de Florida y las
        regulaciones federales aplicables a la actividad de seguros en los
        Estados Unidos.
      </p>
    </div>;
}